import { anonymousClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { organizationClient } from "better-auth/client/plugins";
import { queryClient } from "./query-client";

/**
 * Better Auth client for frontend
 * Configured to work with the API gateway
 */
const authClient = createAuthClient({
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:5100",
  plugins: [
    anonymousClient(),
    organizationClient(),
  ],
});

/**
 * Auth hooks and utilities (original from better-auth)
 */
const {
  signIn: originalSignIn,
  signUp: originalSignUp,
  signOut: originalSignOut,
  useSession,
  useActiveOrganization,
  forgetPassword,
  resetPassword,
  organization,
} = authClient;

/**
 * Wrapped signIn that clears React Query cache after successful login
 */
const signIn = {
  email: async (credentials: { email: string; password: string }) => {
    const result = await originalSignIn.email(credentials);

    // Clear all queries on successful sign in (new user session)
    if (result?.data) {
      // Clear everything except session, then invalidate session to refetch
      await queryClient.removeQueries({
        predicate: (query) => !query.queryKey.includes('better-auth')
      });
      // Force refetch session to update UI immediately
      await queryClient.invalidateQueries({ queryKey: ['better-auth'] });
      console.log("[AUTH] Cleared React Query cache and refetched session after sign in");
    }

    return result;
  },
  social: originalSignIn.social,
  anonymous: async () => {
    const result = await originalSignIn.anonymous();

    // Clear all queries on successful anonymous sign in
    if (result?.data) {
      // Clear everything except session, then invalidate session to refetch
      await queryClient.removeQueries({
        predicate: (query) => !query.queryKey.includes('better-auth')
      });
      // Force refetch session to update UI immediately
      await queryClient.invalidateQueries({ queryKey: ['better-auth'] });
      console.log("[AUTH] Cleared React Query cache and refetched session after anonymous sign in");
    }

    return result;
  },
};

/**
 * Wrapped signUp that clears React Query cache after successful signup
 */
const signUp = {
  email: async (data: { name: string; email: string; password: string }) => {
    const result = await originalSignUp.email(data);

    // Clear all queries on successful sign up (new user session)
    if (result?.data) {
      // Clear everything except session, then invalidate session to refetch
      await queryClient.removeQueries({
        predicate: (query) => !query.queryKey.includes('better-auth')
      });
      // Force refetch session to update UI immediately
      await queryClient.invalidateQueries({ queryKey: ['better-auth'] });
      console.log("[AUTH] Cleared React Query cache and refetched session after sign up");
    }

    return result;
  },
};

/**
 * Wrapped signOut that clears React Query cache after logout
 */
const signOut = async () => {
  const result = await originalSignOut();

  // Clear all cached data on sign out
  await queryClient.clear();
  console.log("[AUTH] Cleared all React Query cache after sign out");

  return result;
};

/**
 * Social sign-in helpers
 * Note: These redirect to OAuth provider, so cache is cleared on redirect back
 */
export const signInWithGoogle = async () => {
  // Clear cache before redirecting to OAuth
  await queryClient.clear();
  console.log("[AUTH] Cleared React Query cache before Google OAuth");

  await authClient.signIn.social({
    provider: "google",
    callbackURL: "/",
  });
};

export const signInWithGitHub = async () => {
  // Clear cache before redirecting to OAuth
  await queryClient.clear();
  console.log("[AUTH] Cleared React Query cache before GitHub OAuth");

  await authClient.signIn.social({
    provider: "github",
    callbackURL: "/",
  });
};

/**
 * Anonymous/Guest user helpers
 */
export const signInAsGuest = async () => {
  return await authClient.signIn.anonymous();
};

export {
  authClient,
  signIn,
  signUp,
  signOut,
  useSession,
  useActiveOrganization,
  forgetPassword,
  resetPassword,
  organization,
};
